import React, {useEffect, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import Loader from "../../../../components/template/loader.jsx";
import GarrisonService from "../../../../services/dispatch/garrison-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const CloseGarrisonModal = ({payload, onClose, onSuccess}) => {
    const navigate = navigateHook();
    const {configuration} = useDashboardContext();
    const [compositionRows, setCompositionRows] = useState([]);
    const [finalObservation, setFinalObservation] = useState("");
    const [schedulesJustification, setSchedulesJustification] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requested = Boolean(payload);
    const garrison = payload?.garrison;
    const garrisonId = garrison?.garrisonId;

    const closeQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.closedGarrison(garrisonId),
        queryFn: () => GarrisonService.getClosedGarrison(garrisonId),
        enabled: requested && Boolean(garrisonId),
    });
    const isReady = !closeQuery.isFetching && (closeQuery.isSuccess || closeQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) return;
        setFinalObservation("");
        setSchedulesJustification("");
        setIsSubmitting(false);
    }, [requested]);

    useEffect(() => {
        const rows = (closeQuery.data?.compositions ?? []).map(composition => ({
            id: composition.id,
            name: composition.name,
            kmControl: composition.kmControl,
            finalMileage: configuration.isSuggestKm && composition.kmControl && composition.actualKm !== undefined && composition.actualKm !== null
                ? String(composition.actualKm)
                : "",
            mileageWarning: "",
            allowInvalidMileage: false,
        }));
        setCompositionRows(rows);
    }, [closeQuery.data?.compositions, configuration.isSuggestKm]);

    const setCompositionMileage = (id, value) =>
        setCompositionRows(prev => prev.map(row => row.id === id ? {...row, finalMileage: value, mileageWarning: "", allowInvalidMileage: false} : row));

    const setAllowInvalidMileage = (id, value) =>
        setCompositionRows(prev => prev.map(row => row.id === id ? {...row, allowInvalidMileage: value} : row));

    const validateMileage = async () => {
        const warnings = {};
        let isValid = true;

        for (const row of compositionRows) {
            if (!row.kmControl || row.finalMileage === "") continue;

            const result = await GarrisonService.checkKmByCompositionCloseGarrison(row.id, row.finalMileage);
            const warning = result?.invalid ? String(result.message ?? "").replaceAll("<br/>", "\n") : "";

            warnings[row.id] = warning;
            if (warning && !row.allowInvalidMileage) isValid = false;
        }

        setCompositionRows(prev => prev.map(row => ({...row, mileageWarning: warnings[row.id] ?? ""})));
        return isValid;
    };

    const handleSubmit = async () => {
        if (!closeQuery.data) {
            projectUtils.swalErrorMessage("Erro", "A guarnição selecionada já foi encerrada.", "Ok");
            return;
        }

        if (closeQuery.data.justifySchedules && !schedulesJustification) {
            projectUtils.swalErrorMessage("", "A justificativa é obrigatória.", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            const isMileageValid = await validateMileage();
            if (!isMileageValid) {
                setIsSubmitting(false);
                return;
            }

            await GarrisonService.closeGarrison({
                id: garrisonId,
                composition: compositionRows.map(row => ({
                    id: row.id,
                    finalMileage: row.finalMileage ? row.finalMileage : null,
                })),
                finalObservation,
                schedulesJustification,
            });

            closeModal();
            projectUtils.swalSuccessMessage(
                "success",
                "Sucesso",
                "Guarnição encerrada com sucesso.",
                "Ok",
                true,
                "Editar",
                ({isDismissed}) => {
                    if (isDismissed) {
                        navigate(`${DESPACHO_WEB_URL}/garrison/form/${garrisonId}?redir=despacho/dashboard`, true);
                    }

                    onSuccess();
                },
                false,
                false,
                "#fff",
                {cancelButton: "swal-edit-button"},
            );
        } catch (error) {
            console.error("Erro ao encerrar guarnição pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível encerrar a guarnição";
            projectUtils.swalErrorMessage("Erro", message, "Ok", false, null, undefined, true);
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title="Encerrar guarnição" className="w-full max-w-2xl" isCloseClick={false} animated={false}>
            <Modal.Body className="relative flex max-h-[calc(100vh-180px)] flex-col gap-4 overflow-auto">
                {closeQuery.isError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">Não foi possível carregar os dados da guarnição.</div>}
                {!closeQuery.isError && (
                    <>
                        <div><p className="text-[14px] font-bold text-[#676a6c]">Guarnição</p><span className="text-sm text-[#555]">{closeQuery.data?.name ?? garrison?.garrisonName}</span></div>
                        {compositionRows.map(row => (
                            <div key={row.id} className="border-t border-[#ddd] pt-3">
                                <p className="text-[14px] font-bold text-[#676a6c]">Composição</p>
                                <span className="text-sm text-[#555]">{row.name}</span>
                                {row.kmControl && (
                                    <InputForm
                                        classNameWrapper={"mt-3"}
                                        label="Km final"
                                        type="number"
                                        integerOnly
                                        value={row.finalMileage}
                                        min={1}
                                        onChange={({target}) => setCompositionMileage(row.id, target.value)}
                                    />
                                )}
                                {row.mileageWarning && (
                                    <div className="mt-2 rounded border border-[#f8ac59] bg-[#fff7ed] p-2 text-[12px] text-[#8a5a20]">
                                        <p className="whitespace-pre-line">{row.mileageWarning}</p>
                                        <label className="mt-2 flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={row.allowInvalidMileage}
                                                onChange={({target}) => setAllowInvalidMileage(row.id, target.checked)}
                                            />
                                            <span>Salvar mesmo assim</span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        ))}
                        <InputTextarea label="Observação final" value={finalObservation} onChange={({target}) => setFinalObservation(target.value)} height={120}/>
                        {closeQuery.data?.justifySchedules && (
                            <InputTextarea
                                label="Justificativa de atividades não realizadas *"
                                value={schedulesJustification}
                                onChange={({target}) => setSchedulesJustification(target.value)}
                                height={120}
                            />
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" onClick={handleSubmit} loading={isSubmitting} disabled={closeQuery.isError || closeQuery.isLoading} actionFeedbackType='save'>Salvar</Button>
            </Modal.Footer>
            </Modal>
        </>
    );
};

export default CloseGarrisonModal;